<!--ag-grid表头组件-->
<template>
  <div
    class="ag-head-cont-ex"
    :class="params.customClass ? { [params.customClass]: true } : {}"
  >
    <div
      class="ag-cell-label-container"
      :class="{
        'ag-header-cell-sorted-asc': sort == 'asc',
        'ag-header-cell-sorted-desc': sort == 'desc',
        'ag-header-cell-sorted-none': !sort,
        'ag-header-cell-sorted-mixed': sortMixed
      }"
      role="presentation"
    >
      <span
        v-if="params.enableMenu"
        ref="eMenu"
        class="ag-header-icon ag-header-cell-menu-button"
        :class="{ 'ag-header-menu-always-show': suppressMenuHide }"
        aria-hidden="true"
        @click="showMenu"
        ><span
          class="ag-icon ag-icon-menu"
          unselectable="on"
          role="presentation"
        ></span
      ></span>

      <!--suffix slot-->
      <component
        :is="params.suffixSlot"
        v-if="params.suffixSlot"
        :params="params"
      />

      <!-- 组合列 -->
      <ElTooltip v-if="params.complex" placement="top" content="显示设置">
        <Icons.easyColumnSetting
          style="cursor: pointer"
          @click.stop="configComplexColumn"
        />
      </ElTooltip>

      <div
        ref="eLabel"
        class="ag-header-cell-label"
        role="presentation"
        @click="onHeaderCellClick"
      >
        <!--prefix slot-->
        <component
          :is="params.prefixSlot"
          v-if="params.prefixSlot"
          :params="params"
        />

        <span v-if="params.textAlignRight" style="flex: 1" />
        <span v-if="params.redstar" class="redstar">*</span>

        <!-- help -->
        <ElTooltip v-if="params.helpText" placement="top">
          <template #content>
            <pre style="display: inline">{{ params.helpText }}</pre>
          </template>
          <ElIcon><QuestionFilled /></ElIcon>
        </ElTooltip>

        <span ref="eText" class="ag-header-cell-text">{{
          params.displayName
        }}</span>

        <span
          ref="eFilter"
          class="ag-header-icon ag-header-label-icon ag-filter-icon"
          :class="{ 'ag-hidden': !enableFilter }"
          aria-hidden="true"
        >
          <span
            class="ag-icon ag-icon-filter"
            unselectable="on"
            role="presentation"
          >
          </span>
        </span>
        <span v-if="params.enableSorting" class="ag-sort-indicator-container">
          <span
            class="ag-sort-indicator-icon ag-sort-order"
            :class="{ 'ag-hidden': !showIndex }"
          >
            <span>{{ sortIndex }}</span>
          </span>

          <span
            ref="eSortAsc"
            class="ag-sort-indicator-icon ag-sort-ascending-icon"
            :class="{ 'ag-hidden': sort !== 'asc' }"
            aria-hidden="true"
          >
            <span
              class="ag-icon ag-icon-asc"
              unselectable="on"
              role="presentation"
            ></span>
          </span>
          <span
            ref="eSortDesc"
            class="ag-sort-indicator-icon ag-sort-descending-icon"
            :class="{ 'ag-hidden': sort !== 'desc' }"
            aria-hidden="true"
          >
            <span
              class="ag-icon ag-icon-desc"
              unselectable="on"
              role="presentation"
            ></span>
          </span>
          <span
            ref="eSortMixed"
            class="ag-sort-indicator-icon ag-sort-mixed-icon"
            :class="{ 'ag-hidden': sort && !alwaysHideNoSort }"
            aria-hidden="true"
            ><span
              class="ag-icon ag-icon-none"
              unselectable="on"
              role="presentation"
            ></span
          ></span>
          <span
            ref="eSortNone"
            class="ag-sort-indicator-icon ag-sort-none-icon"
            :class="{ 'ag-hidden': !sort || alwaysHideNoSort }"
            aria-hidden="true"
          ></span>
        </span>

        <!--label slot-->
        <component
          :is="params.labelSlot"
          v-if="params.labelSlot"
          :params="params"
        />
      </div>
    </div>

    <!--slot blow text-->
    <component
      :is="params.belowSlot"
      v-if="params.belowSlot"
      :params="params"
    />
  </div>
</template>
<script lang="ts" setup>
import { ref, VNode } from 'vue';
import { ElIcon } from 'element-plus';
import { QuestionFilled } from '@element-plus/icons-vue';
import { IHeaderParams } from 'ag-grid-community';
import { Icons } from '../../common/Icons';
import { showDialog } from '../../core/EasyPopup';
import ComplexColumnConfig from '../table-config/ComplexColumnConfig.vue';

const props = defineProps<{
  params: IHeaderParams & {
    customClass?: string; // 顶层class
    textAlignRight?: boolean; // 文本居右
    helpText?: string; // 帮助文字
    redstar?: boolean; // 显示红色星号

    // 复合列
    complex?: {
      tableName: [string, string]; // 表格名, 如['search', 'sales-order']
      labels: Record<string, string>; // id => title
      visibles: () => Record<string, boolean>; // id => visible
    };

    prefixSlot?: () => VNode; // 前插槽
    suffixSlot?: () => VNode; // 后插槽
    labelSlot?: () => VNode; // 文字后插槽
    belowSlot?: () => VNode; // 下方插槽
  };
}>();
// eslint-disable-next-line vue/no-setup-props-destructure
const api = props.params.api;
// eslint-disable-next-line vue/no-setup-props-destructure
const column = props.params.column;
const columnModel = (props.params.columnApi as any).columnModel;
const gridOptionsWrapper = (api as any).gridOptionsWrapper;
const sortController = (api as any).sortController;
const menuFactory = (api as any).menuFactory;

// 是否隐藏Filter
const enableFilter = ref(column.isFilterActive());
column.addEventListener('filterChanged', () => {
  enableFilter.value = column.isFilterActive();
});

// sortIndex
const sortIndex = ref('');
const showIndex = ref(false);
// 排序
const sort = ref(column.getSort());
const sortMixed = ref(false);

const alwaysHideNoSort =
  !column.getColDef().unSortIcon && !gridOptionsWrapper.isUnSortIcon();

const isColumnShowingRowGroup = column.getColDef().showRowGroup;

// 列排序变化处理
function onSortChanged() {
  // 1. 计算sortIndex
  const allColumnsWithSorting = sortController.getColumnsWithSortingOrdered();

  const indexThisCol =
    sortController.getDisplaySortIndexForColumn(column) ?? -1;
  const moreThanOneColSorting = allColumnsWithSorting.some(
    (col: any) => sortController.getDisplaySortIndexForColumn(col) ?? -1 >= 1
  );
  showIndex.value = indexThisCol >= 0 && moreThanOneColSorting;

  if (indexThisCol >= 0) {
    sortIndex.value = (indexThisCol + 1).toString();
  } else {
    sortIndex.value = '';
  }

  // 2. sort值
  sort.value = column.getSort();

  // 3. mixed
  if (isColumnShowingRowGroup) {
    const sourceColumns = columnModel.getSourceColumnsForGroupColumn(column);
    // this == is intentional, as it allows null and undefined to match, which are both unsorted states
    const sortDirectionsMatch = sourceColumns?.every(
      (sourceCol: any) => column.getSort() == sourceCol.getSort()
    );
    sortMixed.value = !sortDirectionsMatch;
  }
}

if (props.params.enableSorting) {
  onSortChanged();
  api.addEventListener('sortChanged', onSortChanged);
  api.addEventListener('columnRowGroupChanged', onSortChanged);
}

let lastMovingChanged = 0;
column.addEventListener('movingChanged', () => {
  lastMovingChanged = new Date().getTime();
});
const sortUsingCtrl = gridOptionsWrapper.isMultiSortKeyCtrl();
const onHeaderCellClick = (event: MouseEvent) => {
  if (!props.params.enableSorting) return;

  // sometimes when moving a column via dragging, this was also firing a clicked event.
  // here is issue raised by user: https://ag-grid.zendesk.com/agent/tickets/1076
  // this check stops sort if a) column is moving or b) column moved less than 200ms ago (so caters for race condition)
  const moving = column.isMoving();
  const nowTime = new Date().getTime();
  // typically there is <2ms if moving flag was set recently, as it would be done in same VM turn
  const movedRecently = nowTime - lastMovingChanged < 50;
  const columnMoving = moving || movedRecently;

  if (!columnMoving) {
    const multiSort = sortUsingCtrl
      ? event.ctrlKey || event.metaKey
      : event.shiftKey;

    props.params.progressSort(multiSort);
  }
};

// menu
const suppressMenuHide = gridOptionsWrapper.isSuppressMenuHide();
const eMenu = ref<HTMLElement>();
function showMenu() {
  menuFactory.showMenuAfterButtonClick(column, eMenu.value, 'columnMenu');
}

// 组合列配置
async function configComplexColumn() {
  if (!props.params.complex) return;

  const complex = props.params.complex;

  await showDialog(
    ComplexColumnConfig,
    {
      api: props.params.api,

      // 表格名
      tableName: complex.tableName,
      // 主字段名
      columnName: column.getColDef().field!,

      // id => title
      labels: complex.labels,
      // id => visible
      visibles: complex.visibles
    },

    { title: '显示设置', customStyle: { width: '30%', height: '50%' } }
  );
}
</script>

<style lang="scss" scoped>
.ag-head-cont-ex {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;

  :deep(.ag-header-cell-label i.el-icon) {
    font-size: 14px;
    margin-top: 2px;
  }

  :deep(.redstar) {
    color: red;
    font-size: 16px;
    padding: 3px 4px 0 0;
  }
}
</style>
